/**
 * Turning the chart's bars into the shape the Elliott endpoint takes.
 *
 * <h2>The ordering trap</h2>
 *
 * The chart stores each bar as `[open, close, low, high]` — ECharts' candlestick order, which is
 * NOT the open-high-low-close order the name OHLC implies. Everything else in this app reads those
 * positions individually (`bar[1]` for close, `bar[3]` for high), so the mismatch never surfaces.
 *
 * Sending the array straight through would be the obvious thing to do and would silently transpose
 * high and close for every bar. That does not fail: the engine would label a series that never
 * traded, return a confident count, and nobody would have any reason to doubt it. Hence named
 * fields on the wire, and hence this being its own tested unit rather than three lines inside a
 * click handler.
 */

/** Positions within the chart's own bar array. Named so the mapping below reads as intended. */
const OPEN = 0;
const CLOSE = 1;
const LOW = 2;
const HIGH = 3;

/** Epoch millis for a chart date, or null when it cannot be read as one. */
function toTimestamp(date) {
  if (!date) return null;
  const parsed = Date.parse(typeof date === 'string' ? date.slice(0, 10) : date);
  return Number.isNaN(parsed) ? null : parsed;
}

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The bars to send, in chart order.
 *
 * Bars whose prices are missing or non-numeric are dropped here as well as server-side. The chart's
 * series carries a projection window at the end — dates with no prices, which the studies draw
 * into — and those are not bars the market traded.
 *
 * @param {{dates: Array<string>, ohlc: Array<Array<number>>, volumes?: Array<number>}} barData
 * @returns {Array<{timestamp: number|null, open: number, high: number, low: number, close: number, volume: number}>}
 */
export function toElliottBars(barData) {
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return [];

  const out = [];
  const count = Math.min(barData.dates.length, barData.ohlc.length);

  for (let i = 0; i < count; i++) {
    const bar = barData.ohlc[i];
    if (!Array.isArray(bar) || bar.length < 4) continue;

    const open = bar[OPEN];
    const close = bar[CLOSE];
    const low = bar[LOW];
    const high = bar[HIGH];
    if (!finite(open) || !finite(close) || !finite(low) || !finite(high)) continue;

    const volume = Array.isArray(barData.volumes) ? barData.volumes[i] : 0;
    out.push({
      timestamp: toTimestamp(barData.dates[i]),
      open,
      high,
      low,
      close,
      volume: finite(volume) ? Math.round(volume) : 0,
    });
  }
  return out;
}

/** The full request body for POST /api/charts/elliott-analysis. */
export function buildElliottRequest(barData, { symbol, timeframe = 'Daily' } = {}) {
  return {
    symbol: symbol || barData?.symbol || 'UNKNOWN',
    timeframe,
    bars: toElliottBars(barData),
  };
}

/** Bars below this cannot produce a count, so the round trip is not worth making. */
export const MIN_BARS_FOR_ANALYSIS = 20;
